/**
 * Display formatting for the Admin and POS (CLAUDE.md §12, docs/specs/admin-shell.md): every number, money
 * amount and date a user sees goes through these, so language and the Bengali-numerals setting apply everywhere.
 * Money is never touched as a float: paisa are split into taka and paisa with bigint arithmetic.
 */
export const DEFAULT_TIME_ZONE = "Asia/Dhaka";
const BENGALI_DIGITS = "০১২৩৪৫৬৭৮৯";
/** `bn-BD` groups in lakhs (1,23,456), `en-BD` in thousands; `-u-nu-` picks the digit set. */
function intlLocale({ locale, numerals }) {
    return `${locale === "bn" ? "bn-BD" : "en-BD"}-u-nu-${numerals === "bengali" ? "beng" : "latn"}`;
}
export function toBengaliDigits(text) {
    return text.replace(/[0-9]/g, (digit) => BENGALI_DIGITS[Number(digit)] ?? digit);
}
export function toLatinDigits(text) {
    return text.replace(/[০-৯]/g, (digit) => String(BENGALI_DIGITS.indexOf(digit)));
}
export function formatNumber(value, options) {
    return new Intl.NumberFormat(intlLocale(options)).format(value);
}
/** `125050` paisa → `৳1,250.50` (`৳১,২৫০.৫০` with Bengali numerals); negative → `-৳1,250.50`. */
export function formatMoney(paisa, options) {
    const amount = BigInt(paisa);
    const negative = amount < 0n;
    const absolute = negative ? -amount : amount;
    const taka = formatNumber(absolute / 100n, options);
    const cents = (absolute % 100n).toString().padStart(2, "0");
    const fraction = options.numerals === "bengali" ? toBengaliDigits(cents) : cents;
    return `${negative ? "-" : ""}৳${taka}.${fraction}`;
}
/** A price for shoppers: whole taka without `.00` (`৳1,250`), otherwise like `formatMoney` (`৳1,250.50`). */
export function formatPrice(paisa, options) {
    const amount = BigInt(paisa);
    if (amount % 100n !== 0n)
        return formatMoney(amount, options);
    const negative = amount < 0n;
    return `${negative ? "-" : ""}৳${formatNumber((negative ? -amount : amount) / 100n, options)}`;
}
/**
 * A stock quantity kept in the unit's smallest step (`1250` g of a kg product with precision 3) → `1.250`, with the
 * unit's decimals, grouping and the chosen digits. Accepts the API's decimal strings. Bigint only.
 */
export function formatQuantity(scaled, precision, options) {
    const amount = BigInt(scaled);
    const negative = amount < 0n;
    const absolute = negative ? -amount : amount;
    const step = 10n ** BigInt(precision);
    const whole = formatNumber(absolute / step, options);
    const decimals = (absolute % step).toString().padStart(precision, "0");
    const fraction = options.numerals === "bengali" ? toBengaliDigits(decimals) : decimals;
    return `${negative ? "-" : ""}${whole}${precision > 0 ? `.${fraction}` : ""}`;
}
const QUANTITY_INPUT = /^([+-])?(\d+)(?:\.(\d+))?$/;
/**
 * A typed quantity in the unit (`2.5` kg) → its smallest step (`2500n`), or `null` when it is not a number, has more
 * decimals than the unit allows, or has a sign where none is allowed. Either digit set, grouping commas, spaces.
 */
export function parseQuantity(text, precision, options = {}) {
    const match = QUANTITY_INPUT.exec(toLatinDigits(text).replace(/[,\s]/g, ""));
    if (!match)
        return null;
    const [, sign, whole = "0", decimals = ""] = match;
    if (decimals.length > precision || (sign !== undefined && options.signed !== true))
        return null;
    const value = BigInt(whole + decimals.padEnd(precision, "0"));
    return sign === "-" ? -value : value;
}
const MONEY_INPUT = /^(-)?(\d+)(?:\.(\d{1,2}))?$/;
/**
 * Typed money → paisa, or `null` if it is not an amount. Accepts either digit set, `৳`, grouping commas and
 * spaces, and at most two decimals (`1,250.5` → `125050n`). String parsing only: no floating point.
 */
/** Paisa as the plain decimal taka gateways expect (`107000n` → `"1070.00"`): no symbol, no grouping, Latin digits. */
export function takaDecimal(paisa) {
    const negative = paisa < 0n;
    const absolute = negative ? -paisa : paisa;
    return `${negative ? "-" : ""}${(absolute / 100n).toString()}.${(absolute % 100n).toString().padStart(2, "0")}`;
}
export function parseMoney(text) {
    const cleaned = toLatinDigits(text).replace(/[৳,\s]/g, "");
    const match = MONEY_INPUT.exec(cleaned);
    if (!match)
        return null;
    const [, sign, taka = "0", decimals = ""] = match;
    const paisa = BigInt(taka) * 100n + BigInt(decimals.padEnd(2, "0"));
    return sign === undefined ? paisa : -paisa;
}
function asDate(value) {
    return typeof value === "string" ? new Date(value) : value;
}
/** `24 Oct 2026` style, in the tenant's zone. */
export function formatDate(value, options) {
    return new Intl.DateTimeFormat(intlLocale(options), {
        dateStyle: "medium",
        timeZone: options.timeZone ?? DEFAULT_TIME_ZONE,
    }).format(asDate(value));
}
/** Date and time (hours and minutes), in the tenant's zone. */
export function formatDateTime(value, options) {
    return new Intl.DateTimeFormat(intlLocale(options), {
        dateStyle: "medium",
        timeStyle: "short",
        timeZone: options.timeZone ?? DEFAULT_TIME_ZONE,
    }).format(asDate(value));
}
const CALENDAR_DATE = /^\d{4}-\d{2}-\d{2}$/;
/**
 * The start of a calendar day (`2026-10-24`, as a date input gives it) in a zone, as an ISO instant with offset
 * (`2026-10-24T00:00:00+06:00` in Asia/Dhaka): what API date filters take. The offset is the zone's on that day.
 */
export function zonedDayStart(date, timeZone = DEFAULT_TIME_ZONE) {
    if (!CALENDAR_DATE.test(date))
        throw new RangeError(`Not a calendar date: ${date}`);
    const zoneName = new Intl.DateTimeFormat("en-US", { timeZone, timeZoneName: "longOffset" })
        .formatToParts(new Date(`${date}T12:00:00Z`))
        .find((part) => part.type === "timeZoneName")?.value ?? "GMT";
    const offset = zoneName === "GMT" ? "+00:00" : zoneName.replace("GMT", "");
    return `${date}T00:00:00${offset}`;
}
/** The calendar day an instant falls on in a zone (`2026-10-24T20:00:00Z` → `2026-10-25` in Asia/Dhaka): business dates. */
export function zonedDate(at, timeZone = DEFAULT_TIME_ZONE) {
    return new Intl.DateTimeFormat("en-CA", {
        timeZone,
        year: "numeric",
        month: "2-digit",
        day: "2-digit",
    }).format(at);
}
/** `2026-10-31` + 1 → `2026-11-01` (calendar arithmetic, no time zone involved). */
export function addDays(date, days) {
    if (!CALENDAR_DATE.test(date))
        throw new RangeError(`Not a calendar date: ${date}`);
    const day = new Date(`${date}T00:00:00Z`);
    day.setUTCDate(day.getUTCDate() + days);
    return day.toISOString().slice(0, 10);
}
const PERCENT_INPUT = /^(\d{1,3})(?:\.(\d{1,2}))?$/;
/** Typed percent → basis points (`7.5` → `750`, CLAUDE.md §7), either digit set, 0–100 with up to 2 decimals. */
export function parsePercent(text) {
    const match = PERCENT_INPUT.exec(toLatinDigits(text).replace(/[%\s]/g, ""));
    if (!match)
        return null;
    const [, whole = "0", decimals = ""] = match;
    const bp = Number(whole) * 100 + Number(decimals.padEnd(2, "0"));
    return bp <= 10_000 ? bp : null;
}
/** Basis points → `7.5%` in the user's language and digits. */
export function formatPercent(bp, options) {
    const whole = Math.trunc(bp / 100);
    const decimals = (bp % 100).toString().padStart(2, "0").replace(/0+$/, "");
    const text = decimals === "" ? String(whole) : `${String(whole)}.${decimals}`;
    return `${options.numerals === "bengali" ? toBengaliDigits(text) : text}%`;
}
//# sourceMappingURL=format.js.map